import React, { useState } from "react";
import {
  Box,
  Container,
  IconButton,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

/**
 * Extracts YouTube video ID from various YouTube URL formats
 * @param {string} url - YouTube URL or video ID
 * @returns {string} - YouTube video ID
 */
const extractVideoId = (url) => {
  if (!url) return "";
  
  // If it's already just an ID (no special characters)
  if (!url.includes("youtube") && !url.includes("youtu.be") && !url.includes("/")) {
    return url;
  }
  
  // Handle different YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return url; // Return as-is if no pattern matches
};

const VideoPlayer = ({ videos = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));

  if (!videos || videos.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No videos available
        </Typography>
      </Container>
    );
  }

  // On xl screens, show videos side by side with no carousel controls
  if (isXl) {
    return (
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 3, md: 5 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 3,
            alignItems: "start",
          }}
        >
          {videos.map((video, index) => {
            const videoId = extractVideoId(video);
            return (
              <Paper
                key={`${videoId}-${index}`}
                elevation={3}
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "16/9",
                  bgcolor: "black",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  component="iframe"
                  src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`Video ${index + 1}`}
                />
              </Paper>
            );
          })}
        </Box>
      </Container>
    );
  }

  const currentVideoId = extractVideoId(videos[currentIndex]);
  const hasMultipleVideos = videos.length > 1;

  // Calculate previous and next video indices
  const previousIndex = currentIndex === 0 ? videos.length - 1 : currentIndex - 1;
  const nextIndex = currentIndex === videos.length - 1 ? 0 : currentIndex + 1;
  const previousVideoId = hasMultipleVideos ? extractVideoId(videos[previousIndex]) : null;
  const nextVideoId = hasMultipleVideos ? extractVideoId(videos[nextIndex]) : null;

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? videos.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === videos.length - 1 ? 0 : prev + 1));
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 3, md: 5 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: { xs: "auto", md: "320px" },
          mb: { xs: 3, md: 0 },
          gap: { md: 2 },
        }}
      >
        {/* Left Preview Video - positioned behind main video */}
        {hasMultipleVideos && previousVideoId && (
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              left: 0,
              width: "clamp(180px, 22vw, 260px)",
              aspectRatio: "16/9",
              opacity: 0.45,
              cursor: "pointer",
              transition: "opacity 0.3s",
              zIndex: 0,
              "&:hover": {
                opacity: 0.65,
              },
            }}
            onClick={handlePrevious}
          >
            <Paper
              elevation={2}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                bgcolor: "black",
              }}
            >
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${previousVideoId}?rel=0`}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                  pointerEvents: "none",
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`Previous video preview`}
              />
            </Paper>
          </Box>
        )}

        {/* Main Video Container - overlaps previews by 1/3 of preview width */}
        <Paper
          elevation={3}
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "min(100%, 56.25rem)",
            aspectRatio: "16/9",
            bgcolor: "black",
            zIndex: 1,
            borderRadius: { xs: 1.5, md: 2 },
            overflow: "hidden",
            mx: { xs: "auto", md: 0 },
            boxShadow: { xs: 2, md: 3 },
            px: 0,
          }}
        >
          <Box
            component="iframe"
            src={`https://www.youtube.com/embed/${currentVideoId}?rel=0`}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={`Video ${currentIndex + 1}`}
          />
        </Paper>

        {/* Right Preview Video - positioned behind main video */}
        {hasMultipleVideos && nextVideoId && (
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              right: 0,
              width: "clamp(180px, 22vw, 260px)",
              aspectRatio: "16/9",
              opacity: 0.45,
              cursor: "pointer",
              transition: "opacity 0.3s",
              zIndex: 0,
              "&:hover": {
                opacity: 0.65,
              },
            }}
            onClick={handleNext}
          >
            <Paper
              elevation={2}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                bgcolor: "black",
              }}
            >
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${nextVideoId}?rel=0`}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                  pointerEvents: "none",
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`Next video preview`}
              />
            </Paper>
          </Box>
        )}

        {/* Left Arrow */}
        {hasMultipleVideos && (
          <IconButton
            onClick={handlePrevious}
            sx={{
              color: "text.primary",
              position: "absolute",
              opacity: { xs: 0.6, md: 1 },
              left: { xs: "2%", md: "5%" },
              zIndex: 2,
              bgcolor: { xs: "rgba(255,255,255,0.92)", md: "transparent" },
              width: { xs: 48, md: 54 },
              height: { xs: 48, md: 54 },
              "& svg": {
                fontSize: { xs: "1.1rem", md: "1.2rem" },
              },
              "&:hover": {
                bgcolor: {
                  xs: "rgba(255, 255, 255, 0.75)",
                  md: "action.hover",
                },
              },
            }}
            aria-label="Previous video"
          >
            <ArrowBackIosIcon />
          </IconButton>
        )}

        {/* Right Arrow */}
        {hasMultipleVideos && (
          <IconButton
            onClick={handleNext}
            sx={{
              color: "text.primary",
              position: "absolute",
              opacity: { xs: 0.6, md: 1 },
              right: { xs: "2%", md: "5%" },
              zIndex: 2,
              bgcolor: { xs: "rgba(255,255,255,0.92)", md: "transparent" },
              width: { xs: 48, md: 54 },
              height: { xs: 48, md: 54 },
              "& svg": {
                fontSize: { xs: "1.1rem", md: "1.2rem" },
              },
              "&:hover": {
                bgcolor: {
                  xs: "rgba(255, 255, 255, 0.75)",
                  md: "action.hover",
                },
              },
            }}
            aria-label="Next video"
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}
      </Box>
    </Container>
  );
};

export default VideoPlayer;
